import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Clapperboard, Clock, Type, Hash, Download, CheckCircle2, XCircle, Loader2, Upload, ImageIcon, Lightbulb, Check, UserRound, Coins, Cpu, AlertTriangle, Mic, Smartphone, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { analyzeProduct, scriptFromAngle, generateVideo, getVideoStatus, getVideoByRequest, getAvatars, getCreditBalance, countWords, estimateDuration, WORDS_PER_SECOND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  createSubmitGuard, intentSignature, legacyReceiptKey, prepareLegacyReceipt, readLegacyReceipt,
  clearLegacyReceipt, classifySubmitError, VIDEO_DEFINITIVE_DETAILS, LEGACY_RECONCILING_NOTICE,
} from "@/lib/legacyRequest";
import { useHeygenCreation } from "@/lib/useHeygenCreation";
import { HEYGEN_DISABLED_NOTICE } from "@/lib/providerFeatures";

const DURATIONS = [
  { value: "30", label: "30 detik" },
  { value: "45", label: "45 detik" },
  { value: "60", label: "60 detik" },
];

const STYLES = ["Jualan", "Review Produk", "Promo"];

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png"];

const STATUS_META = {
  processing: { label: "Memproses", color: "text-blue-400", icon: Loader2 },
  failed: { label: "Gagal", color: "text-red-400", icon: XCircle },
  reconciling: { label: "Memeriksa status", color: "text-amber-400", icon: Loader2 },
};

const formatDuration = (secs) => {
  if (secs < 60) return `${secs} detik`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m} mnt ${s} dtk` : `${m} menit`;
};

export default function CreateVideo() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState("Jualan");
  const [language, setLanguage] = useState("Indonesia");
  const [duration, setDuration] = useState("60");
  const [aspectRatio, setAspectRatio] = useState("9:16");

  const [script, setScript] = useState("");
  const [angles, setAngles] = useState([]);
  const [productInfo, setProductInfo] = useState(null);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [scripting, setScripting] = useState(false);

  const [videoStatus, setVideoStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [generatedRatio, setGeneratedRatio] = useState("9:16");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const heygen = useHeygenCreation();
  const { user } = useAuth();
  const submitGuard = useRef(createSubmitGuard());
  const videoReceiptKey = legacyReceiptKey("video", user?.id);
  const [avatarList, setAvatarList] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState("");
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditsPerSecond, setCreditsPerSecond] = useState(1);
  const [reserveBufferPercent, setReserveBufferPercent] = useState(0);

  const refreshBalance = () => {
    getCreditBalance()
      .then((d) => {
        setCreditBalance(d.credit_balance);
        if (d.credits_per_second) setCreditsPerSecond(d.credits_per_second);
        if (typeof d.reserve_buffer_percent === "number") setReserveBufferPercent(d.reserve_buffer_percent);
      })
      .catch(() => {});
  };

  useEffect(() => {
    getAvatars()
      .then((d) => {
        const list = d.avatars || [];
        setAvatarList(list);
        const sys = list.find((a) => a.is_system && a.status === "active") || list[0] || null;
        if (sys) setSelectedAvatarId(sys.id);
      })
      .catch(() => {});
    refreshBalance();
  }, []);

  const selectedAvatar = avatarList.find((a) => a.id === selectedAvatarId) || null;
  const isMemberAvatar = selectedAvatar && !selectedAvatar.is_system;
  const memberNotReady = isMemberAvatar && selectedAvatar.status !== "ready";
  const memberNoAvatarIII = isMemberAvatar && selectedAvatar.status === "ready" && !selectedAvatar.avatar_iii_supported;
  const avatarBlocked = memberNotReady || memberNoAvatarIII;

  const words = countWords(script);
  const chars = script.length;
  const estSecs = estimateDuration(script);
  const estCreditSeconds = Math.max(1, Math.ceil(words / WORDS_PER_SECOND));
  const estimatedCredit = script.trim() === "" ? 0 : Math.ceil(estCreditSeconds * creditsPerSecond);
  const reservedSeconds = Math.max(estCreditSeconds, Math.ceil(estCreditSeconds * (100 + reserveBufferPercent) / 100));
  const reservedCredit = script.trim() === "" ? 0 : Math.ceil(reservedSeconds * creditsPerSecond);
  const insufficientCredit =
    creditBalance !== null && reservedCredit > 0 && creditBalance < reservedCredit;

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const clearVideoReceipt = () => clearLegacyReceipt(window.sessionStorage, videoReceiptKey);

  // Poll an accepted job by its provider id (settles credit server-side on a final state).
  const startStatusPolling = (videoId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await getVideoStatus(videoId);
        if (s.status === "completed") {
          setVideoStatus("completed");
          setVideoUrl(s.video_url || null);
          stopPolling();
          clearVideoReceipt();
          refreshBalance();
        } else if (s.status === "failed") {
          setVideoStatus("failed");
          stopPolling();
          clearVideoReceipt();
          refreshBalance();
        }
      } catch (e) {
        // transient error while polling
      }
    }, 3500);
  };

  // Poll an intent that has no provider id yet (in flight, unconfirmed, or after a lost response).
  // This only reads the outcome; it never submits again.
  const startRequestPolling = (requestId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await getVideoByRequest(requestId);
        if (s.status === "failed") {
          setVideoStatus("failed");
          stopPolling();
          clearVideoReceipt();
          refreshBalance();
        } else if (s.video_id && s.status !== "reconciling") {
          setVideoStatus("processing");
          startStatusPolling(s.video_id);
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          // The server never saw this intent: nothing was created or held.
          stopPolling();
          clearVideoReceipt();
          setVideoStatus(null);
          refreshBalance();
          toast.error("Permintaan tidak ditemukan. Silakan coba lagi.");
        }
      }
    }, 4000);
  };

  const followOutcome = (data, requestId) => {
    if (data.status === "failed") {
      clearVideoReceipt();
      setVideoStatus("failed");
    } else if (data.video_id && data.status !== "reconciling") {
      setVideoStatus("processing");
      toast.success("Video sedang diproses.");
      startStatusPolling(data.video_id);
    } else {
      setVideoStatus("reconciling");
      startRequestPolling(requestId);
    }
    refreshBalance();
  };

  // After a reload the receipt is the only memory of an unfinished intent: resume it by reading.
  useEffect(() => {
    const receipt = readLegacyReceipt(window.sessionStorage, videoReceiptKey);
    if (!receipt) return undefined;
    let cancelled = false;
    getVideoByRequest(receipt.request_id).then((s) => {
      if (cancelled) return;
      if (s.status === "failed") clearLegacyReceipt(window.sessionStorage, videoReceiptKey);
      else if (s.video_id && s.status !== "reconciling") { setVideoStatus("processing"); startStatusPolling(s.video_id); }
      else { setVideoStatus("reconciling"); startRequestPolling(receipt.request_id); }
    }).catch((e) => {
      if (!cancelled && e?.response?.status === 404) clearLegacyReceipt(window.sessionStorage, videoReceiptKey);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoReceiptKey]);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Gunakan JPG, JPEG, atau PNG.");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Ukuran foto maksimal 10 MB.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const openPicker = () => fileInputRef.current?.click();

  const runAnalyze = async () => {
    setGenerating(true);
    setVideoStatus(null);
    setSelectedAngle(null);
    setShowEditor(false);
    setScript("");
    try {
      const data = await analyzeProduct(file, style, description);
      setProductInfo({
        product_name: data.product_name,
        product_summary: data.product_summary,
        target_audience: data.target_audience,
        product_image_url: data.product_image_url || null,
      });
      setAngles(data.angles || []);
      toast.success("3 ide konten berhasil dibuat");
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI gagal menganalisis produk. Silakan coba lagi.";
      toast.error(typeof msg === "string" ? msg : "AI gagal menganalisis produk. Silakan coba lagi.");
    } finally {
      setGenerating(false);
    }
  };

  const handleAnalyze = () => {
    if (!file) {
      toast.error("Silakan upload foto produk terlebih dahulu.");
      return;
    }
    runAnalyze();
  };

  const makeScriptFromAngle = async () => {
    if (selectedAngle === null) {
      toast.error("Pilih salah satu angle terlebih dahulu.");
      return;
    }
    const angle = angles[selectedAngle];
    setScripting(true);
    setVideoStatus(null);
    try {
      const data = await scriptFromAngle({
        product_name: productInfo?.product_name || "",
        product_summary: productInfo?.product_summary || "",
        target_audience: productInfo?.target_audience || "",
        description,
        angle_title: angle.title,
        angle_hook: angle.hook,
        style,
        language: language === "English" ? "en" : "id",
        duration: parseInt(duration, 10),
      });
      setScript(data.script || "");
      setShowEditor(true);
      toast.success("Script berhasil dibuat");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Script belum berhasil dibuat. Silakan coba lagi.";
      toast.error(typeof msg === "string" ? msg : "Script belum berhasil dibuat. Silakan coba lagi.");
    } finally {
      setScripting(false);
    }
  };

  const handleRegenerate = () => {
    if (selectedAngle === null) {
      toast.error("Pilih salah satu angle terlebih dahulu.");
      return;
    }
    makeScriptFromAngle();
  };

  const handleGenerateVideo = async () => {
    if (!heygen.enabled) {
      toast.error(HEYGEN_DISABLED_NOTICE);
      return;
    }
    if (script.trim() === "") {
      toast.error("Skrip masih kosong");
      return;
    }
    if (insufficientCredit) {
      toast.error("Kredit tidak cukup untuk membuat video ini.");
      return;
    }
    if (memberNotReady) {
      toast.error("Avatar belum siap. Tunggu hingga avatar selesai diproses.");
      return;
    }
    if (memberNoAvatarIII) {
      toast.error("Avatar III tidak tersedia untuk avatar ini.");
      return;
    }
    if (saving || videoStatus === "processing" || videoStatus === "reconciling") return;
    // Synchronous claim: the state checked above is stale until the next render, so a fast
    // double click would otherwise pass it twice.
    if (!submitGuard.current.tryClaim()) return;
    const payload = {
      script,
      language: language === "English" ? "en" : "id",
      duration: parseInt(duration, 10),
      aspect_ratio: aspectRatio,
      product_name: productInfo?.product_name || "",
      product_image_url: productInfo?.product_image_url || undefined,
      avatar_id: selectedAvatarId || undefined,
    };
    let receipt;
    try {
      // The request id is stored BEFORE sending; the same inputs always reuse it.
      receipt = prepareLegacyReceipt(window.sessionStorage, videoReceiptKey, intentSignature(payload));
    } catch (e) {
      toast.error(e.message);
      submitGuard.current.release();
      return;
    }
    setSaving(true);
    setVideoUrl(null);
    setVideoStatus(null);
    setGeneratedRatio(aspectRatio);
    try {
      const data = await generateVideo({ ...payload, client_request_id: receipt.request_id });
      followOutcome(data, receipt.request_id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (classifySubmitError(e, VIDEO_DEFINITIVE_DETAILS) === "definitive") {
        // Rejected before anything was created or held: the next attempt is a new intent.
        clearVideoReceipt();
        toast.error(typeof detail === "string" ? detail : "Gagal membuat video. Silakan coba lagi.");
        setVideoStatus(null);
        refreshBalance();
      } else {
        // The job may exist. Keep the receipt (a retry replays it) and check the outcome by reading.
        toast.warning("Status permintaan belum pasti. Jangan kirim ulang; kami sedang memeriksa.");
        setVideoStatus("reconciling");
        startRequestPolling(receipt.request_id);
      }
    } finally {
      setSaving(false);
      submitGuard.current.release();
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Buat Video</p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
          Bikin Video Jualan.
          <br />
          Semudah Upload Foto.
        </h1>
        <p className="mt-2 text-slate-500 md:text-lg">
          Upload foto produk, biarkan AI membuat script videonya.
        </p>
      </div>

      {heygen.loaded && !heygen.enabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" role="status" data-testid="heygen-disabled-notice">
          {HEYGEN_DISABLED_NOTICE} Untuk membuat video baru, gunakan menu Video Jualan.
        </div>
      )}

      {/* Step 1: Produk */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 space-y-6">
        <div className="space-y-1">
          <h2 className="font-heading text-lg font-semibold text-slate-900">1. Produk</h2>
          <p className="text-sm text-slate-500">Upload foto produk agar AI memahami produk Anda. Deskripsi bersifat opsional.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-700">Foto Produk</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="product-photo-input"
          />

          {!file ? (
            <button
              type="button"
              onClick={openPicker}
              data-testid="product-photo-dropzone"
              className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Upload className="h-6 w-6" />
              </span>
              <span className="font-heading font-semibold text-slate-800">Upload Foto Produk</span>
              <span className="text-sm text-slate-500">JPG, JPEG, atau PNG • maksimal 10 MB</span>
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row items-start gap-4 rounded-xl border border-slate-200 p-4">
              <img
                src={previewUrl}
                alt="Preview produk"
                data-testid="product-preview"
                className="h-40 w-40 rounded-lg object-cover border border-slate-200"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-500">Nama file</p>
                <p className="font-medium text-slate-800 truncate" data-testid="product-filename">{file.name}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openPicker}
                  data-testid="change-photo-btn"
                  className="mt-3 active:scale-95 transition-transform"
                >
                  <ImageIcon className="h-4 w-4 mr-2" /> Ganti Foto
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Deskripsi Produk (opsional) */}
        <div className="space-y-2">
          <Label htmlFor="desc" className="text-slate-700">Deskripsi Produk</Label>
          <Textarea
            id="desc"
            data-testid="product-description"
            rows={4}
            placeholder="Paste deskripsi produk di sini (opsional)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="resize-y text-sm"
          />
          <p className="text-xs text-slate-400">Semakin lengkap informasi produk, semakin akurat script AI.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label className="text-slate-700">Gaya Video</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="style-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STYLES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`style-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700">Bahasa</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Indonesia" data-testid="lang-id">Indonesia</SelectItem>
                <SelectItem value="English" data-testid="lang-en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700">Durasi</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value} data-testid={`duration-${d.value}`}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          data-testid="analyze-product-btn"
          onClick={handleAnalyze}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
        >
          {generating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {generating ? "AI sedang menganalisis produk..." : "Buat 3 Ide Konten"}
        </Button>
      </div>

      {/* 3 Ide Konten + pilih angle */}
      {angles.length > 0 && (
        <div className="space-y-4" data-testid="ideas-list">
          <div className="space-y-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">2. Pilih Angle</h2>
            <p className="text-sm text-slate-500">Pilih satu angle. AI akan membuat script berdasarkan angle yang Anda pilih.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {angles.map((a, i) => {
              const isSelected = selectedAngle === i;
              return (
                <div
                  key={i}
                  data-testid={`idea-card-${i}`}
                  onClick={() => setSelectedAngle(i)}
                  className={`cursor-pointer rounded-xl border bg-white p-5 space-y-2 transition-colors ${
                    isSelected ? "border-blue-500 ring-2 ring-blue-500/30" : "border-slate-200 hover:border-blue-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-600">
                      <Lightbulb className="h-4 w-4" />
                      <span className="text-xs font-semibold uppercase tracking-wide">Ide {i + 1}</span>
                    </div>
                    {isSelected && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                        data-testid={`angle-selected-${i}`}
                      >
                        <Check className="h-3 w-3" /> Angle Dipilih
                      </span>
                    )}
                  </div>
                  <p className="font-heading font-semibold text-slate-900">{a.title}</p>
                  <p className="text-sm text-slate-500">{a.hook}</p>
                  <Button
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    data-testid={`use-angle-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAngle(i);
                    }}
                    className={`mt-2 w-full active:scale-95 transition-transform ${
                      isSelected ? "bg-blue-600 hover:bg-blue-700 text-white" : ""
                    }`}
                  >
                    {isSelected ? "Angle Dipilih" : "Gunakan Angle Ini"}
                  </Button>
                </div>
              );
            })}
          </div>

          {selectedAngle !== null && (
            <Button
              data-testid="make-script-btn"
              onClick={makeScriptFromAngle}
              disabled={scripting}
              className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
            >
              {scripting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              {scripting ? "Membuat Script..." : "Buat Script dari Angle Ini"}
            </Button>
          )}
        </div>
      )}

      {/* Script Editor */}
      {showEditor && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 space-y-4" data-testid="script-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-slate-900">3. Script Video</h2>
          </div>
          <p className="-mt-2 text-sm text-slate-500">
            Script dibuat AI dan bisa Anda edit. Script yang sudah diedit inilah yang akan digunakan sebagai suara video.
          </p>
          <Textarea
            data-testid="script-textarea"
            rows={10}
            placeholder="Script video akan muncul di sini..."
            value={script}
            onChange={(e) => setScript(e.target.value)}
            className="resize-y font-mono text-sm leading-relaxed"
          />

          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-600" data-testid="word-count">
              <Type className="h-4 w-4 text-slate-400" /> {words} kata
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-600" data-testid="char-count">
              <Hash className="h-4 w-4 text-slate-400" /> {chars} karakter
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-600" data-testid="est-duration">
              <Clock className="h-4 w-4 text-slate-400" /> ~{formatDuration(estSecs)}
            </span>
          </div>

          <div className="pt-2 space-y-1">
            <h3 className="font-heading text-base font-semibold text-slate-900">4. Avatar & Format</h3>
            <p className="text-sm text-slate-500">Pilih avatar yang akan tampil di video, lalu pilih format videonya.</p>
          </div>

          <div className="space-y-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-3" data-testid="avatar-selection">
            <Label className="text-slate-700 flex items-center gap-2">
              <UserRound className="h-4 w-4 text-blue-600" /> Pilih Avatar
            </Label>
            <Select value={selectedAvatarId} onValueChange={setSelectedAvatarId}>
              <SelectTrigger data-testid="avatar-select">
                <SelectValue placeholder="Pilih avatar" />
              </SelectTrigger>
              <SelectContent>
                {avatarList.map((a) => {
                  const disabled = !a.is_system && a.status !== "ready";
                  const label = a.is_system
                    ? `${a.name} (Avatar Default)`
                    : `${a.name}${a.status === "ready" ? "" : a.status === "processing" ? " (Sedang diproses)" : " (Gagal)"}`;
                  return (
                    <SelectItem key={a.id} value={a.id} disabled={disabled} data-testid={`avatar-option-${a.id}`}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {selectedAvatar && (
              <div className="text-sm space-y-1" data-testid="selected-avatar-info">
                {selectedAvatar.is_system ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" /> {selectedAvatar.name} — Avatar Default
                  </span>
                ) : (
                  <>
                    {selectedAvatar.status === "ready" && (
                      <span className="inline-flex items-center gap-1.5 text-emerald-600" data-testid="selected-avatar-ready">
                        <CheckCircle2 className="h-4 w-4" /> {selectedAvatar.name} — Siap
                      </span>
                    )}
                    {selectedAvatar.status === "processing" && (
                      <span className="inline-flex items-center gap-1.5 text-blue-600" data-testid="selected-avatar-processing">
                        <Loader2 className="h-4 w-4 animate-spin" /> {selectedAvatar.name} — Sedang diproses...
                      </span>
                    )}
                    {selectedAvatar.status === "failed" && (
                      <span className="inline-flex items-center gap-1.5 text-red-600" data-testid="selected-avatar-failed">
                        <XCircle className="h-4 w-4" /> {selectedAvatar.name} — Gagal dibuat
                      </span>
                    )}
                    {selectedAvatar.status === "ready" && (
                      selectedAvatar.avatar_iii_supported ? (
                        <span className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="selected-avatar-iii-yes">
                          <Cpu className="h-3.5 w-3.5" /> Avatar III tersedia
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs text-amber-600" data-testid="selected-avatar-iii-no">
                          <AlertTriangle className="h-3.5 w-3.5" /> Avatar III tidak tersedia untuk avatar ini
                        </span>
                      )
                    )}
                    {selectedAvatar.status === "ready" && (
                      <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="selected-avatar-voice">
                        <Mic className="h-3.5 w-3.5" /> Voice: {selectedAvatar.default_voice_name || (selectedAvatar.default_voice_id ? "Tersimpan" : "Voice default sistem")}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-3" data-testid="aspect-select">
            <Label className="text-slate-700 flex items-center gap-2">
              <Clapperboard className="h-4 w-4 text-blue-600" /> Format Video
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { v: "9:16", t: "Vertical", hint: "TikTok • Reels • Shorts • Shopee Video", Icon: Smartphone },
                { v: "16:9", t: "Landscape", hint: "YouTube • Facebook • Video Panjang", Icon: Monitor },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`aspect-${opt.v.replace(":", "-")}`}
                  onClick={() => setAspectRatio(opt.v)}
                  className={`text-left rounded-lg border px-3 py-2.5 bg-white transition-colors ${
                    aspectRatio === opt.v ? "border-blue-500 ring-2 ring-blue-500/30" : "border-slate-200 hover:border-blue-300"
                  }`}
                >
                  <span className="flex items-center gap-2 font-semibold text-slate-900">
                    <opt.Icon className="h-4 w-4 text-blue-600" /> {opt.v} — {opt.t}
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">{opt.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <h3 className="pt-2 font-heading text-base font-semibold text-slate-900">5. Buat Video</h3>

          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-sm"
            data-testid="credit-info"
          >
            <span className="inline-flex items-center gap-1.5 text-slate-700" data-testid="credit-available">
              <Coins className="h-4 w-4 text-blue-600" />
              Kredit tersedia: <span className="font-semibold text-slate-900">{creditBalance === null ? "—" : creditBalance}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-700" data-testid="credit-estimate">
              Estimasi penggunaan: <span className="font-semibold text-slate-900">~{estimatedCredit} kredit</span>
            </span>
            {insufficientCredit && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600" data-testid="credit-insufficient">
                <XCircle className="h-3.5 w-3.5" /> Kredit tidak cukup untuk membuat video ini.
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              variant="outline"
              data-testid="regenerate-script-btn"
              onClick={handleRegenerate}
              disabled={scripting}
              className="active:scale-95 transition-transform"
            >
              {scripting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Generate Ulang
            </Button>
            <Button
              data-testid="generate-video-btn"
              onClick={handleGenerateVideo}
              disabled={!heygen.enabled || saving || videoStatus === "processing" || videoStatus === "reconciling" || insufficientCredit || avatarBlocked}
              className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
            >
              {saving || videoStatus === "processing" || videoStatus === "reconciling" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Clapperboard className="h-4 w-4 mr-2" />
              )}
              {saving || videoStatus === "processing" || videoStatus === "reconciling" ? "Membuat Video..." : "Generate Video"}
            </Button>
          </div>
        </div>
      )}

      {/* Video Result */}
      {videoStatus && <VideoResult status={videoStatus} videoUrl={videoUrl} ratio={generatedRatio} />}
    </div>
  );
}

const VideoResult = ({ status, videoUrl, ratio }) => {
  if (status === "completed") {
    const isPortrait = ratio !== "16:9";
    const boxClass = isPortrait
      ? "w-full max-w-xs aspect-[9/16]"
      : "w-full max-w-2xl aspect-video";
    return (
      <div className="space-y-4" data-testid="video-result">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Hasil Video</h2>
        <p className="inline-flex items-center gap-2 text-emerald-600 font-medium" data-testid="video-status-label">
          <CheckCircle2 className="h-5 w-5" /> Video selesai
        </p>
        <div className="flex flex-col items-center gap-4">
          <div
            className={`${boxClass} overflow-hidden rounded-2xl bg-slate-900 flex items-center justify-center`}
            data-testid={`video-box-${isPortrait ? "portrait" : "landscape"}`}
          >
            {videoUrl ? (
              <video
                data-testid="video-player"
                src={videoUrl}
                controls
                playsInline
                className="h-full w-full object-contain"
              />
            ) : (
              <p className="text-sm text-slate-400 px-6 text-center">URL video tidak tersedia.</p>
            )}
          </div>
          {videoUrl && (
            <a
              data-testid="download-video-btn"
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              download
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 px-4 py-2 text-sm font-medium text-white transition-colors active:scale-95"
            >
              <Download className="h-4 w-4" /> Download Video
            </a>
          )}
        </div>
      </div>
    );
  }

  const meta = STATUS_META[status] || STATUS_META.processing;
  const Icon = meta.icon;
  return (
    <div className="space-y-3" data-testid="video-result">
      <h2 className="font-heading text-lg font-semibold text-slate-900">Hasil Video</h2>
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center px-6">
          <Icon className={`h-12 w-12 ${meta.color} ${status === "processing" ? "animate-spin" : ""}`} />
          <p className="font-heading text-xl font-semibold text-white" data-testid="video-status-label">
            {meta.label}
          </p>
          <p className="text-sm text-slate-400 max-w-sm">
            {status === "processing" && "Video sedang dibuat..."}
            {status === "reconciling" && LEGACY_RECONCILING_NOTICE}
            {status === "failed" && "Gagal membuat video. Silakan coba lagi."}
          </p>
        </div>
      </div>
    </div>
  );
};
